/**
 * Circuit Breaker Progress Bar Component
 *
 * Visual indicator showing how close daily P&L is to triggering the circuit breaker.
 * - Green when positive P&L
 * - Yellow/Orange as approaching threshold
 * - Red and glowing when at or near threshold
 * - Shows percentage of threshold reached
 */

import React from 'react';
import { motion } from 'framer-motion';

interface CircuitBreakerBarProps {
  dailyPnL: number;              // Today's P&L in dollars (per-account)
  startOfDayBalance: number;     // Balance at start of day
  thresholdPct: number;          // Loss threshold (e.g., 2 for -2%)
  isTriggered?: boolean;         // Whether circuit breaker is active
  globalDailyPnl?: number;       // Global P&L across all user accounts
  dailyProfitLimit?: number;     // Profit target threshold (0 = disabled)
}

export function CircuitBreakerBar({
  dailyPnL,
  startOfDayBalance,
  thresholdPct = 2,
  isTriggered = false,
  globalDailyPnl = 0,
  dailyProfitLimit = 0,
}: CircuitBreakerBarProps) {
  // Calculate daily P&L percentage
  const dailyPnLPct = startOfDayBalance > 0
    ? (dailyPnL / startOfDayBalance) * 100
    : 0;

  // Calculate how much of the threshold is reached (0-100%)
  // Positive P&L = 0%, -2% loss with 2% threshold = 100%
  const thresholdProgress = dailyPnL >= 0
    ? 0
    : Math.min(100, (Math.abs(dailyPnLPct) / thresholdPct) * 100);

  // Determine bar color based on progress
  const getBarColor = () => {
    if (isTriggered) return 'bg-red-600';
    if (dailyPnL >= 0) return 'bg-green-500';
    if (thresholdProgress < 50) return 'bg-yellow-500';
    if (thresholdProgress < 80) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Determine glow intensity
  const shouldGlow = thresholdProgress >= 80 || isTriggered;

  return (
    <motion.div
      className="mt-4 mb-2"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      {/* Label */}
      <div className="flex items-center justify-between mb-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium">
          {isTriggered ? (
            <span className="text-red-600 dark:text-red-400 font-semibold">
              🚨 Circuit Breaker Active
            </span>
          ) : (
            'Daily Loss Monitor'
          )}
        </span>
        <span>
          {dailyPnL >= 0 ? (
            <span className="text-green-600 dark:text-green-400">
              Safe (+{Math.abs(dailyPnLPct).toFixed(2)}%)
            </span>
          ) : (
            <span className={thresholdProgress >= 80 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
              {Math.abs(dailyPnLPct).toFixed(2)}% / -{thresholdPct}%
            </span>
          )}
        </span>
      </div>

      {/* Progress Bar Container */}
      <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        {/* Progress Fill */}
        <motion.div
          className={`h-full rounded-full ${getBarColor()} ${
            shouldGlow ? 'shadow-lg shadow-red-500/50 animate-pulse' : ''
          }`}
          initial={{ width: 0 }}
          animate={{ width: `${thresholdProgress}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />

        {/* Threshold Marker (at 100%) */}
        {!isTriggered && thresholdProgress < 100 && (
          <div
            className="absolute top-0 right-0 w-0.5 h-full bg-red-600 opacity-50"
            title={`-${thresholdPct}% Threshold`}
          />
        )}
      </div>

      {/* Warning Message */}
      {thresholdProgress >= 80 && !isTriggered && (
        <motion.p
          className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          ⚠️ Approaching loss limit - trades will be blocked at -{thresholdPct}%
        </motion.p>
      )}

      {isTriggered && !(dailyProfitLimit > 0 && globalDailyPnl >= dailyProfitLimit) && (
        <motion.p
          className="mt-2 text-xs text-red-600 dark:text-red-400 font-semibold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          🛑 Loss limit reached — new buys blocked until midnight PT
        </motion.p>
      )}

      {isTriggered && dailyProfitLimit > 0 && globalDailyPnl >= dailyProfitLimit && (
        <motion.p
          className="mt-2 text-xs text-green-600 dark:text-green-400 font-semibold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          🎯 Profit target reached — new buys paused until midnight PT
        </motion.p>
      )}
    </motion.div>
  );
}
